import React from 'react';
import { Input } from 'reactstrap';
import './ReportTable.css';

// Table for the Crude Reports module.
//
// Why not react-data-table-component (used everywhere else in this app): these
// reports need TWO-LEVEL headers — "Informasi Umum" / "Yield & Klasifikasi"
// groups on General, one group per CDU (Status / %-Vol / MB-bln) on Evaluation.
// RDT cannot render colspan header rows, so the table is hand-rolled — but it
// is styled to be visually identical to RDT (see ReportTable.css).
//
// Pagination and sorting are SERVER-driven: the owning page supplies data /
// totalRows and the handlers. `key` on a sortable leaf must be the API's
// PascalCase sortField (anything outside the whitelist -> 400).
//
// Column shape:
//   leaf  -> { key, header, accessor, sortable?, align?, width? }
//   group -> { header, children: [leaf, ...] }

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const flattenLeaves = columns =>
  columns.flatMap(col => (col.children ? col.children : [col]));

// Same header label markup the RDT-based pages use, so the type matches.
const headerLabel = label => (
  <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
    {label}
  </span>
);

const ReportTable = ({
  columns = [],
  data = [],
  loading = false,
  emptyText = 'No data found',
  totalRows = 0,
  page = 1,
  perPage = 10,
  sortField = null,
  sortDirection = null,
  onChangePage,
  onChangeRowsPerPage,
  onSort,
}) => {
  const leaves = flattenLeaves(columns);
  const hasGroups = columns.some(col => col.children);

  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const from = totalRows ? (page - 1) * perPage + 1 : 0;
  const to = Math.min(page * perPage, totalRows);

  const sortIconClass = key => {
    if (sortField !== key) return 'ri-arrow-up-down-line';
    return sortDirection === 'desc' ? 'ri-arrow-down-line' : 'ri-arrow-up-line';
  };

  const headerCell = (leaf, rowSpan) => {
    const sortable = leaf.sortable !== false && !!leaf.key;
    return (
      <th
        key={leaf.key ?? leaf.header}
        rowSpan={rowSpan}
        style={{ width: leaf.width }}
        className={`${sortable ? 'report-table-sortable' : ''} ${
          leaf.align === 'center' ? 'text-center' : ''
        }`}
        onClick={sortable ? () => onSort(leaf.key) : undefined}
        data-testid={sortable ? `sort-${leaf.key}` : undefined}
      >
        {headerLabel(leaf.header)}
        {sortable && (
          <>
            {' '}
            <i
              className={`${sortIconClass(leaf.key)} align-bottom report-sort-icon ${
                sortField === leaf.key ? 'is-active' : ''
              }`}
            ></i>
          </>
        )}
      </th>
    );
  };

  return (
    <React.Fragment>
      <div className='table-responsive'>
        <table className='report-table'>
          <thead>
            <tr>
              {columns.map(col =>
                col.children ? (
                  <th
                    key={col.header}
                    colSpan={col.children.length}
                    className='text-center'
                  >
                    {headerLabel(col.header)}
                  </th>
                ) : (
                  headerCell(col, hasGroups ? 2 : 1)
                )
              )}
            </tr>
            {hasGroups && (
              <tr>
                {columns
                  .filter(col => col.children)
                  .flatMap(col => col.children)
                  .map(leaf => headerCell(leaf, 1))}
              </tr>
            )}
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={leaves.length} className='text-center py-4'>
                  Loading...
                </td>
              </tr>
            )}

            {!loading && data.length === 0 && (
              <tr>
                <td
                  colSpan={leaves.length}
                  className='text-center py-4 text-muted'
                >
                  <i
                    className='ri-database-2-line d-block'
                    style={{ fontSize: '32px', marginBottom: '8px' }}
                  ></i>
                  {emptyText}
                </td>
              </tr>
            )}

            {!loading &&
              data.map((row, idx) => (
                <tr key={row.id ?? idx} data-testid={`row-${row.id ?? idx}`}>
                  {leaves.map(leaf => (
                    <td
                      key={leaf.key ?? leaf.header}
                      className={leaf.align === 'center' ? 'text-center' : ''}
                    >
                      {leaf.accessor(row)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Mirrors RDT's pagination bar: right-aligned, rows-per-page select,
          "from-to of total", then first / prev / next / last buttons. */}
      <nav className='report-pagination'>
        <div className='report-pagination-group'>
          <span>Rows per page:</span>
          <Input
            type='select'
            className='form-select form-select-sm report-pagination-select'
            value={perPage}
            onChange={e => onChangeRowsPerPage(Number(e.target.value))}
            aria-label='Rows per page'
          >
            {PER_PAGE_OPTIONS.map(opt => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Input>
        </div>

        <span>
          {from}-{to} of {totalRows}
        </span>

        <div className='report-pagination-group' style={{ gap: 0 }}>
          <button
            type='button'
            className='report-pagination-btn'
            aria-label='First Page'
            disabled={page <= 1}
            onClick={() => onChangePage(1)}
          >
            <i className='ri-arrow-left-double-line'></i>
          </button>
          <button
            type='button'
            className='report-pagination-btn'
            aria-label='Previous Page'
            disabled={page <= 1}
            onClick={() => onChangePage(page - 1)}
          >
            <i className='ri-arrow-left-s-line'></i>
          </button>
          <button
            type='button'
            className='report-pagination-btn'
            aria-label='Next Page'
            disabled={page >= totalPages}
            onClick={() => onChangePage(page + 1)}
          >
            <i className='ri-arrow-right-s-line'></i>
          </button>
          <button
            type='button'
            className='report-pagination-btn'
            aria-label='Last Page'
            disabled={page >= totalPages}
            onClick={() => onChangePage(totalPages)}
          >
            <i className='ri-arrow-right-double-line'></i>
          </button>
        </div>
      </nav>
    </React.Fragment>
  );
};

export default ReportTable;
